/**
 * Function that manages URL search parameters for category and language selection.
 * It provides reactive references for the selected category and language, and synchronizes them
 * with the URL search parameters.
 * This does not allow for setting the URL search parameters directly.
 *
 * @returns {UseSearchParams} An object containing:
 * - `selectedCategory`: A reactive reference to the selected news category.
 * - `selectedLang`: A reactive reference to the selected language or languages.
 *
 * @remarks
 * - The `category` parameter defaults to `'News'` if it is invalid or not provided.
 * - The `lang` parameter can be a string or an array of strings,
 *   but only the first value is used if it is an array.
 * - Every caller shares one URL state. The URL is rewritten only when the
 *   query string actually changes, so a page load produces at most one
 *   `history.replaceState` call (the one that adds the default category)
 *   instead of one per caller.
 */
import type { EffectScope, Ref } from 'vue'
import { effectScope, reactive, watch } from 'vue'
import {
  searchParamsNewsCategory,
  type SearchParamsNewsCategory,
} from '../../utils/newsCategoryTab'

interface UseSearchParams {
  selectedCategory: Ref<SearchParamsNewsCategory>
  selectedLang: Ref<string | string[] | undefined>
}

type UrlParamValue = string | string[] | undefined

type UrlParams = Record<string, UrlParamValue> & {
  category?: string | undefined
  lang?: string | string[] | undefined
}

let shared: { params: UrlParams; scope: EffectScope } | null = null

function readLocationParams(): UrlParams {
  const params: UrlParams = {}
  const search = new URLSearchParams(window.location.search)
  for (const key of new Set(search.keys())) {
    const values = search.getAll(key)
    params[key] = values.length > 1 ? values : (values[0] ?? '')
  }
  return params
}

function serialize(params: UrlParams): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, item))
    } else {
      search.set(key, value)
    }
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

function syncFromLocation() {
  if (!shared) return
  const fromUrl = readLocationParams()
  // Params that left the URL become undefined, which serialize() drops.
  for (const key of Object.keys(shared.params)) {
    if (!(key in fromUrl)) shared.params[key] = undefined
  }
  Object.assign(shared.params, fromUrl)
}

/**
 * One reactive view of the query string for the whole app. The first caller
 * seeds it, from the URL when it has any parameter and from `initialValue`
 * otherwise, exactly as one vueuse `useUrlSearchParams` instance would.
 * Later callers get the same object, re-read from the URL if the router moved
 * the page since the last sync.
 */
function useSharedUrlParams(initialValue: UrlParams): UrlParams {
  if (typeof window === 'undefined') return reactive({ ...initialValue })

  if (shared) {
    if (window.location.search !== serialize(shared.params)) syncFromLocation()
    return shared.params
  }

  const scope = effectScope(true)
  const params = scope.run(() => {
    const fromUrl = readLocationParams()
    const state = reactive<UrlParams>(
      Object.keys(fromUrl).length > 0 ? fromUrl : { ...initialValue }
    )

    const writeIfChanged = () => {
      const next = serialize(state)
      if (next === window.location.search) return
      window.history.replaceState(
        window.history.state,
        window.document.title,
        window.location.pathname + next + window.location.hash
      )
    }

    watch(state, writeIfChanged, { deep: true })
    // A bare URL gets the seeded default written once, the same way the
    // first vueuse instance used to do it. A URL that already carries its
    // params is left alone.
    writeIfChanged()

    window.addEventListener('popstate', syncFromLocation)
    return state
  })!

  shared = { params, scope }
  return params
}

/** Test hook: forget the shared URL state so the next call re-reads the URL. */
export function resetSharedSearchParams() {
  if (!shared) return
  shared.scope.stop()
  window.removeEventListener('popstate', syncFromLocation)
  shared = null
}

export function useSearchParams(
  defaultCategory: SearchParamsNewsCategory = 'News'
): UseSearchParams {
  const { omitDefaultCategoryParam } = useAppConfig()

  const urlParams = useSharedUrlParams({
    category: omitDefaultCategoryParam ? undefined : defaultCategory,
    lang: undefined,
  })

  onMounted(() => {
    if (omitDefaultCategoryParam && !urlParams.category) return
    const mounted = searchParamsNewsCategory.parse(urlParams.category)
    if (mounted !== urlParams.category) {
      urlParams.category = omitDefaultCategoryParam
        ? undefined
        : defaultCategory
    }
  })

  const parsed = searchParamsNewsCategory.parse(urlParams.category)
  const selectedCategory = ref<SearchParamsNewsCategory>(
    parsed === urlParams.category ? parsed : defaultCategory
  )

  watch(selectedCategory, (newValue) => {
    urlParams.category =
      omitDefaultCategoryParam && newValue === defaultCategory
        ? undefined
        : newValue
  })

  const selectedLang = ref(urlParams.lang)

  watch(
    selectedLang,
    (newValue) => {
      if (Array.isArray(newValue)) {
        urlParams.lang = newValue[0]
      } else {
        urlParams.lang = newValue
      }
    },
    { immediate: true }
  )

  return { selectedCategory, selectedLang }
}
